import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import LoadingButton from '@mui/lab/LoadingButton'
import { ReactComponent as CloseIcon } from '../../assets/images/Close.svg'

interface OverwriteConfirmDialogProps {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    isLoading: boolean
    recordTitles: string[]
}

const OverwriteConfirmDialog = ({ open, onClose, onConfirm, isLoading, recordTitles }: OverwriteConfirmDialogProps) => {
    const handleClose = () => {
        if (isLoading) return
        onClose()
    }

    const count = recordTitles.length
    const plural = count === 1 ? 'record is' : 'records are'

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="18x600">Overwrite existing records?</Typography>
                    <IconButton
                        aria-label="close"
                        disabled={isLoading}
                        onClick={handleClose}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <Typography variant="14x400" color="figma.primary">
                        The following {plural} already on this roadmap. Re-importing will overwrite their current data:
                    </Typography>
                    <Stack component="ul" gap={0.5} sx={{ m: 0, pl: 3 }}>
                        {recordTitles.map((title, index) => (
                            <Typography
                                // Titles can repeat, so pair with the index for a stable key.
                                key={`${title}-${index}`}
                                component="li"
                                variant="14x400"
                                color="figma.grayscale.textHier2"
                            >
                                {title}
                            </Typography>
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button variant="outlined" onClick={handleClose} disabled={isLoading} sx={{ borderRadius: '14px' }}>
                    Cancel
                </Button>
                <LoadingButton
                    loading={isLoading}
                    variant="contained"
                    color="primary"
                    onClick={onConfirm}
                    sx={{ borderRadius: '14px', px: 3 }}
                >
                    Overwrite
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}

export default OverwriteConfirmDialog
